/**
 * Session lifecycle on top of the store, the client and the refresher (FRONTEND_PLAN §7.1–7.2).
 *
 *  - bootstrap(): on page load, try the cookie (`POST /auth/refresh`); if it works, load capabilities.
 *  - login()/logout(): the obvious pair. logout always clears locally, even if the server call fails.
 *  - Proactive renew: a timer fires `expires_in − 60 s` after every session body so an idle tab
 *    never waits for a 401. Paused while hidden; runs immediately when the tab becomes visible.
 *  - Cross-tab: a BroadcastChannel propagates LOGOUT only. Each tab refreshes on its own; the
 *    backend rotates the cookie atomically (`SELECT … FOR UPDATE`), so this is safe.
 */
import { ApiError } from '@/api/errors'
import type { ApiClient } from '@/api/client'
import { authEndpoints } from '@/api/endpoints/auth'
import { capabilitiesEndpoints } from '@/api/endpoints/capabilities'
import type { LoginRequest, SessionResponse } from '@/api/types'
import type { Refresher } from './refresh'
import { useSessionStore, type LogoutReason, type SessionState } from './session-store'

export const RENEW_LEAD_MS = 60_000
const CHANNEL_NAME = 'recoup-session'

export interface SessionServiceDeps {
  readonly api: ApiClient
  readonly refresher: Refresher
  readonly store?: typeof useSessionStore
  readonly now?: () => number
  readonly channel?: Pick<BroadcastChannel, 'postMessage' | 'addEventListener' | 'close'> | null
  readonly document?: Pick<
    Document,
    'visibilityState' | 'addEventListener' | 'removeEventListener'
  > | null
}

export interface SessionService {
  /** Restores a session from the refresh cookie. Resolves to the resulting status. */
  bootstrap: () => Promise<SessionState['status']>
  login: (credentials: LoginRequest) => Promise<SessionResponse>
  logout: (reason?: LogoutReason) => Promise<void>
  /** Loads capabilities for the current session. */
  loadCapabilities: () => Promise<void>
  /** Stops timers and listeners (tests, HMR). */
  dispose: () => void
}

interface TabMessage {
  readonly type: 'logout'
  readonly reason: LogoutReason
}

export function createSessionService(deps: SessionServiceDeps): SessionService {
  const store = deps.store ?? useSessionStore
  const now = deps.now ?? (() => Date.now())
  const auth = authEndpoints(deps.api)
  const capabilities = capabilitiesEndpoints(deps.api)
  const channel = deps.channel === undefined ? openChannel() : deps.channel
  const doc =
    deps.document === undefined
      ? typeof document === 'undefined'
        ? null
        : document
      : deps.document

  let renewTimer: ReturnType<typeof setTimeout> | null = null

  function clearTimer(): void {
    if (renewTimer !== null) clearTimeout(renewTimer)
    renewTimer = null
  }

  function scheduleRenew(): void {
    clearTimer()
    const { expiresAt, status } = store.getState()
    if (status !== 'authenticated' || expiresAt === null) return
    if (doc?.visibilityState === 'hidden') return // re-armed on visibilitychange
    const delay = Math.max(expiresAt - now() - RENEW_LEAD_MS, 0)
    renewTimer = setTimeout(() => {
      void deps.refresher.refresh().then(scheduleRenew)
    }, delay)
  }

  function onVisibility(): void {
    if (doc?.visibilityState !== 'visible') {
      clearTimer()
      return
    }
    const { expiresAt, status } = store.getState()
    if (status !== 'authenticated' || expiresAt === null) return
    // If we slept past the renew point, refresh now; otherwise just re-arm the timer.
    if (expiresAt - now() <= RENEW_LEAD_MS) void deps.refresher.refresh().then(scheduleRenew)
    else scheduleRenew()
  }

  function onTabMessage(event: MessageEvent<TabMessage>): void {
    if (event.data.type === 'logout') {
      clearTimer()
      store.getState().clear('other-tab')
    }
  }

  doc?.addEventListener('visibilitychange', onVisibility)
  channel?.addEventListener('message', onTabMessage as EventListener)

  // Any session body — from login, bootstrap or a 401-triggered refresh — re-arms the timer.
  const unsubscribe = store.subscribe((state, previous) => {
    if (state.accessToken !== previous.accessToken) scheduleRenew()
  })

  async function loadCapabilities(): Promise<void> {
    store.getState().setCapabilities(await capabilities.read())
  }

  /**
   * A scoped user with no locations gets 403 here (ADR-029). That is a valid, authenticated
   * session with nothing it may do — recorded as `capabilitiesDenied`, never thrown.
   */
  async function loadCapabilitiesTolerant(): Promise<void> {
    try {
      await loadCapabilities()
    } catch (error) {
      if (error instanceof ApiError && error.status === 403)
        store.getState().setCapabilitiesDenied(true)
      else throw error
    }
  }

  return {
    async bootstrap() {
      const outcome = await deps.refresher.refresh()
      if (outcome.kind !== 'ok') {
        // The refresher's onOutcome already cleared the store for definitive failures; a transient
        // error with no prior session is still "anonymous" as far as routing is concerned.
        if (store.getState().status === 'unknown') store.getState().clear(null)
        return store.getState().status
      }
      await loadCapabilitiesTolerant()
      return store.getState().status
    },

    async login(credentials) {
      const session = await auth.login(credentials)
      store.getState().setSession(session, now())
      await loadCapabilitiesTolerant()
      return session
    },

    async logout(reason = 'user') {
      clearTimer()
      try {
        await auth.logout()
      } catch {
        // Local state is cleared regardless; the cookie is short-lived and revocable server-side.
      }
      store.getState().clear(reason)
      channel?.postMessage({ type: 'logout', reason } satisfies TabMessage)
    },

    loadCapabilities,

    dispose() {
      clearTimer()
      unsubscribe()
      doc?.removeEventListener('visibilitychange', onVisibility)
      channel?.close()
    },
  }
}

function openChannel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME)
}
